/**
 * <DetailExtras> — kind-specific projection of a DiscoverDetail.extras
 * blob into a readable summary block. Each kind exposes different shape
 * so we switch.
 */

import { Badge } from '@/components/ui/badge';
import type { DiscoverKind, DiscoverDetail } from './catalog-browse-page';

interface Props {
  kind: DiscoverKind;
  detail: DiscoverDetail;
}

function arr(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string');
}

export function DetailExtras({ kind, detail }: Props) {
  const e = detail.extras;

  if (kind === 'tool') {
    const sideEffects = arr(e.sideEffects);
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Tool details</h4>
        <dl className="grid grid-cols-[100px_1fr] gap-y-1 text-xs">
          <dt className="text-muted-foreground">Source</dt>
          <dd className="font-mono">{String(e.sourceType ?? 'builtin')}</dd>
          <dt className="text-muted-foreground">Policy</dt>
          <dd>
            <Badge
              variant="outline"
              className={
                e.policy === 'forbidden'
                  ? 'border-destructive text-destructive'
                  : e.policy === 'require_approval'
                    ? 'border-amber-500/40 text-amber-700 dark:text-amber-400'
                    : 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400'
              }
            >
              {String(e.policy ?? 'auto_approve').replace('_', ' ')}
            </Badge>
          </dd>
          <dt className="text-muted-foreground">Side effects</dt>
          <dd className="flex flex-wrap gap-1">
            {sideEffects.length === 0 ? (
              <span className="text-muted-foreground italic">none declared</span>
            ) : (
              sideEffects.map((s) => (
                <Badge key={s} variant="outline" className="text-[10px]">
                  {s}
                </Badge>
              ))
            )}
          </dd>
        </dl>
        {!!e.inputSchema && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Input schema (JSON)
            </summary>
            <pre className="bg-muted p-2 rounded mt-1 overflow-x-auto text-[10px]">
              {JSON.stringify(e.inputSchema, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  }

  if (kind === 'skill') {
    const tools = arr(e.toolDependencies);
    const fns = arr(e.functionDependencies);
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Skill details</h4>
        {tools.length > 0 && (
          <div>
            <div className="text-[11px] text-muted-foreground mb-1">
              Tools used ({tools.length})
            </div>
            <div className="flex flex-wrap gap-1">
              {tools.slice(0, 12).map((t) => (
                <Badge key={t} variant="outline" className="text-[10px] font-mono">
                  {t}
                </Badge>
              ))}
              {tools.length > 12 && (
                <span className="text-[10px] text-muted-foreground">+{tools.length - 12} more</span>
              )}
            </div>
          </div>
        )}
        {fns.length > 0 && (
          <div>
            <div className="text-[11px] text-muted-foreground mb-1">
              Functions used ({fns.length})
            </div>
            <div className="flex flex-wrap gap-1">
              {fns.slice(0, 12).map((t) => (
                <Badge key={t} variant="outline" className="text-[10px] font-mono">
                  {t}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {!!e.instructions && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Instructions
            </summary>
            <pre className="bg-muted p-2 rounded mt-1 whitespace-pre-wrap text-[11px]">
              {String(e.instructions)}
            </pre>
          </details>
        )}
      </div>
    );
  }

  if (kind === 'prompt') {
    const variables = Array.isArray(e.variables) ? (e.variables as Array<{ name: string }>) : [];
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Prompt details</h4>
        <div className="text-[11px] text-muted-foreground">
          Variables ({variables.length}):
        </div>
        <div className="flex flex-wrap gap-1">
          {variables.length === 0 ? (
            <span className="text-[11px] italic text-muted-foreground">none</span>
          ) : (
            variables.map((v) => (
              <Badge key={v.name} variant="outline" className="text-[10px] font-mono">
                {v.name}
              </Badge>
            ))
          )}
        </div>
        {!!e.template && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Template
            </summary>
            <pre className="bg-muted p-2 rounded mt-1 whitespace-pre-wrap text-[11px]">
              {String(e.template)}
            </pre>
          </details>
        )}
      </div>
    );
  }

  if (kind === 'function') {
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Function details</h4>
        <dl className="grid grid-cols-[100px_1fr] gap-y-1 text-xs">
          <dt className="text-muted-foreground">Function key</dt>
          <dd className="font-mono">{String(e.functionKey ?? detail.slug)}</dd>
        </dl>
        {!!e.inputSchema && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Input schema
            </summary>
            <pre className="bg-muted p-2 rounded mt-1 text-[10px] overflow-x-auto">
              {JSON.stringify(e.inputSchema, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  }

  if (kind === 'agent') {
    const tools = arr(e.assignedTools);
    const skills = arr(e.assignedSkills);
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Agent details</h4>
        <dl className="grid grid-cols-[100px_1fr] gap-y-1 text-xs">
          <dt className="text-muted-foreground">Model brand</dt>
          <dd className="font-mono">{String(e.modelBrand ?? 'default')}</dd>
          <dt className="text-muted-foreground">Tools</dt>
          <dd>{tools.length}</dd>
          <dt className="text-muted-foreground">Skills</dt>
          <dd>{skills.length}</dd>
          {!!e.kind && (
            <>
              <dt className="text-muted-foreground">Kind</dt>
              <dd className="font-mono">{String(e.kind)}</dd>
            </>
          )}
        </dl>
        {!!e.systemPrompt && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              System prompt
            </summary>
            <pre className="bg-muted p-2 rounded mt-1 whitespace-pre-wrap text-[11px]">
              {String(e.systemPrompt).slice(0, 2000)}
              {String(e.systemPrompt).length > 2000 && '...'}
            </pre>
          </details>
        )}
      </div>
    );
  }

  if (kind === 'connection') {
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Connection details</h4>
        <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-xs">
          <dt className="text-muted-foreground">Marketplace cat.</dt>
          <dd>{String(e.marketplaceCategory ?? '-')}</dd>
          {e.isPublished !== undefined && (
            <>
              <dt className="text-muted-foreground">Published</dt>
              <dd>{String(e.isPublished)}</dd>
            </>
          )}
          {e.isActive !== undefined && (
            <>
              <dt className="text-muted-foreground">Active</dt>
              <dd>{String(e.isActive)}</dd>
            </>
          )}
        </dl>
      </div>
    );
  }

  return null;
}
